import React from 'react';

import { Box } from '@rbilabs/universal-components';
import { IStore } from 'features/store/pages/store-locator-experiment/ducks/stores';
import { useIntl } from 'react-intl';
import { SceneRendererProps } from 'react-native-tab-view';

import { IRestaurant } from '@rbi-ctg/store';
import { TabView } from 'components/tab-view';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { primitive } from 'styles/constants/primitives';

import StoreCardList, { StoreCardListRef } from '../store-card-list';
import { OnRestaurantHighlight, StoreLocatorActions } from '../types';

const intlIdMap: { [K in OrderLocatorTab]: string } = {
  [OrderLocatorTab.NEARBY]: 'nearby',
  [OrderLocatorTab.RECENT]: 'recents',
  [OrderLocatorTab.FAVORITE]: 'favorite',
  [OrderLocatorTab.DELIVERY]: 'delivery',
};

interface IListViewProps {
  onTabChange: (tab: OrderLocatorTab) => void;
  onSelectStore?: (storeId: string) => void;
  onRestaurantHighlight: OnRestaurantHighlight;
  onFavoriteClick: () => void;
  currentTabKey: OrderLocatorTab;
  currentTabIndex: number;
  currentTabHasError: boolean;
  locatorTabs: OrderLocatorTab[];
  isInSupportForm?: boolean;
  restaurantFocused: IStore | null;
  storesByTab: {
    [K in OrderLocatorTab]: IStore[];
  };
  flatListRef: React.RefObject<StoreCardListRef>;
  storesById: Record<string, IRestaurant>;
  actions: React.MutableRefObject<StoreLocatorActions>;
  tabSearchStatusMap: {
    [K in OrderLocatorTab]: boolean;
  };
}

const StoreCardListContainer = Box.withConfig({
  width: 'full',
  flex: 1,
});

export const ListView: React.FC<IListViewProps> = ({
  onSelectStore,
  onRestaurantHighlight,
  onTabChange,
  isInSupportForm,
  restaurantFocused,
  currentTabKey,
  currentTabIndex,
  currentTabHasError,
  locatorTabs,
  storesByTab,
  tabSearchStatusMap,
  flatListRef,
  storesById,
  onFavoriteClick,
  actions,
}) => {
  const isDesktop = useIsDesktopBreakpoint();
  const { formatMessage } = useIntl();

  const tabs = locatorTabs.map(key => ({
    key,
    title: formatMessage({ id: intlIdMap[key] }),
  }));

  // As we need to pass additional props, we have to use a custom renderScene function instead of SceneMap
  // Do not return inline functions because  If you pass inline functions, it'll re-create the component every render, which will cause the entire route to unmount and remount every change. It's very bad for performance and will also cause any local state to be lost.
  const renderScene = ({ route }: SceneRendererProps & { route: { key: OrderLocatorTab } }) => {
    // Setting display none for desktop we ensure the non current tabs will be completeley disable in terms of accessibility.
    // With this hack we ensure the user will be able to properly navigate the desktop site using the keyboard.
    const desktopDisplay = isDesktop && route.key !== currentTabKey ? 'none' : 'flex';

    return (
      <StoreCardListContainer display={desktopDisplay} backgroundColor={primitive.$white}>
        <StoreCardList
          tab={route.key}
          currentTabHasError={currentTabHasError}
          restaurants={storesByTab[route.key]}
          isSearching={tabSearchStatusMap[route.key]}
          isInSupportForm={isInSupportForm}
          restaurantFocused={restaurantFocused}
          onSelectStore={onSelectStore}
          onRestaurantHighlight={onRestaurantHighlight}
          onFavoriteClick={onFavoriteClick}
          // Only pass the ref to the current tab
          ref={route.key === currentTabKey ? flatListRef : null}
          storesById={storesById}
          actions={actions}
        />
      </StoreCardListContainer>
    );
  };

  return (
    <TabView
      activeTabIndex={currentTabIndex}
      handleTabIndexChange={(index: number) => onTabChange(locatorTabs[index])}
      tabs={tabs}
      // @ts-expect-error TS(2322) FIXME: Type '({ route }: SceneRendererProps & { route: { ... Remove this comment to see the full error message
      renderScene={renderScene}
      backgroundColor={primitive.$white}
    />
  );
};
